import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useOrderServicePageLogic } from '../hook/useOrderServicePageLogic'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgan } from '../../../../hooks/useOrgan'
import useNotification from '../../../../hooks/useNotification'
import useBaseService from '../../../../hooks/services/useBaseService'
import useQueryWithLoader from '../../../../hooks/useQueryWithLoader'
import { entities } from '../../../../constants/entities'
import { useCallback, useEffect, useState } from 'react'
import {
  cleanPayload,
  normalizeFormErrors,
  transformFormDataForApi,
} from '../../../../utils/objectUtils'
import MainLayout from '../../../../layouts/MainLayout'
import PageHeader from '../../../../components/PageHeader'
import Form from '../../../../components/forms/Form'
import SectionHeader from '../../../../components/forms/SectionHeader'
import SimpleBody from '../../../../components/forms/SimpleBody'
import { Alert, Box, Typography } from '@mui/material'
import ItemListSection from '../../../../components/forms/ItemListSection'
import MovementEnum from '../../../../enums/movement-enum'

const AddItemOrderServicePage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()

  const [contractAmount, setContractAmount] = useState(null)

  const { form, order, equipments, items, extras } = useOrderServicePageLogic({
    isEditPage: false,
    isAddItemPage: true,
    contractAmount,
  })

  const { selectedOrgan } = useOrgan()
  const { serviceOrderId } = useParams()

  // Cria um objeto URLSearchParams para pegar os parâmetros da query
  const queryParams = new URLSearchParams(location.search)

  const contractId =
    queryParams.get('contract_id') || order.formData?.contract_id?.value

  const { showNotification } = useNotification()
  const {
    post: baseServicePost,
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    formErrors,
  } = useBaseService(navigate)

  const { data: contractAmountResponse } = useQueryWithLoader({
    queryKey: ['contractAmountDetails', contractId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.contractAmount(contractId),
        {
          deleted_at: false,
        }
      )
      return response.result
    },
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  })

  useEffect(() => {
    setContractAmount(contractAmountResponse)
  }, [contractAmountResponse])

  const { data: contractOsDetails } = useQueryWithLoader({
    queryKey: ['contractOsDetails', contractId, serviceOrderId],
    queryFn: async () => {
      if (!contractId && serviceOrderId) {
        const response = await baseServiceGetByColumn(
          entities.serviceOrders.getById(serviceOrderId)
        )
        return response.result
      } else if (contractId && serviceOrderId) {
        const response = await baseServiceGet(
          entities.contracts.contractServiceOrder(contractId, serviceOrderId)
        )
        return response.result
      }
    },
    enabled: (!!contractId || !!serviceOrderId) ?? false,
    staleTime: 5 * 60 * 1000,
  })

  // Mutações TanStack Query
  const AddItemsOsMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      form.setErrors({})
      const response = await baseServicePost(
        entities.serviceOrders.addItems(serviceOrderId),
        dataToCreate
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        order.resetOrderServiceForm()
        // voltar a para pagina anterior
        navigate(-1, {
          state: { type: 'success' },
        })

        // Invalida OrdersServicePage
        queryClient.invalidateQueries({ queryKey: ['orders'] })

        // Invalida CreateOrderServicePage
        queryClient.invalidateQueries({ queryKey: ['contractDetails'] })
        queryClient.invalidateQueries({ queryKey: ['contractItems'] })

        // Invalida ContractOsPage
        queryClient.invalidateQueries({ queryKey: ['contractOrders'] })

        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
        queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement'],
        })
      }
    },
    onError: (err) => {
      console.log('onError ', err)
      if (err.response?.data?.errors) {
        form.setErrors(err.response.data.errors)
      }
    },
  })

  const handleSubmit = useCallback(async () => {
    try {
      if (equipments.data.length < 1 && items.data.length < 1) {
        showNotification(
          'warning',
          'É preciso adicionar pelo menos um item na Requisição'
        )
        return
      }

      const transformedFormData = transformFormDataForApi(order.formData)
      const cleanedPayload = cleanPayload(transformedFormData)

      const payload = {
        ...cleanedPayload,
        order: {
          ...cleanedPayload.order,
          organization_id: selectedOrgan?.id,
        },
        equipments: [
          // 1. EQUIPAMENTOS NORMAIS + SEUS EXTRAS
          ...(cleanedPayload?.equipments ?? [])?.map((equipment) => {
            // Busca se existem extras que apontam para este equipamento (pelo ID temporário)
            const relatedExtras = (cleanedPayload?.extras ?? [])
              .filter((extra) => extra.equipment_id === equipment.id)
              .map((extra) => ({
                type_id: extra.type_id,
                quantity: extra.quantity,
              }))

            return {
              ...equipment,
              movement_type_id: order.selects.movementType.selectedValue?.value,
              is_exchange: false,
              is_extra: relatedExtras.length > 0, // Indica ao back que este item tem extras
              extras: relatedExtras, // O array que o seu foreach no PHP vai percorrer
              address_id: cleanedPayload.order?.address_id,
              location_id: cleanedPayload.order?.location_id,
            }
          }),
        ],
        items:
          cleanedPayload?.items?.map((item) => ({
            ...item,
            movement_type_id: order.selects.movementType.selectedValue?.value,
            address_id: cleanedPayload.order?.address_id,
            location_id: cleanedPayload.order?.location_id,
          })) ?? [],
      }

      delete payload.extras

      await AddItemsOsMutation.mutateAsync(payload)
    } catch (error) {
      console.error('Erro ao enviar requisição:', {
        status: error.status,
        message: error.message,
      })
    }
  }, [
    AddItemsOsMutation,
    equipments.data.length,
    items.data.length,
    order.formData,
    order.selects.movementType.selectedValue?.value,
    selectedOrgan?.id,
    showNotification,
  ])

  const handleBack = useCallback(() => {
    if (contractId) {
      navigate(`/aluguel/contratos/${contractId}/requisicoes/`)
    } else {
      navigate(`/aluguel/requisicoes/`)
    }
  }, [contractId, navigate])

  const getMostFrequent = (items, key) => {
    if (!items || items.length === 0) return null

    const counts = items.reduce((acc, item) => {
      const val = item[key]
      if (val) {
        // Usamos o objeto como string para a chave do contador, mas guardamos a referência
        const keyStr = JSON.stringify(val)
        acc[keyStr] = acc[keyStr] || { count: 0, data: val }
        acc[keyStr].count += 1
      }
      return acc
    }, {})

    // Retorna o item que teve a maior contagem
    return Object.values(counts).sort((a, b) => b.count - a.count)[0]?.data
  }

  // * Quando os detalhes da OS de contrato forem carregados, preenche o formulário com os dados
  useEffect(() => {
    if (contractOsDetails) {
      const frequentAddress = getMostFrequent(
        contractOsDetails.items,
        'address'
      )

      order.setFormData((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          id: {
            value: contractOsDetails.id,
            label: contractOsDetails.id,
          },
          movement_type_id: {
            value: contractOsDetails.movement_type?.id,
            label: contractOsDetails.movement_type?.name,
          },
          contract_id: {
            value: contractOsDetails.contract?.id,
            label: `${contractOsDetails.contract?.number} - ${contractOsDetails.contract?.name}`,
          },
          recipient_customer_id: {
            value: contractOsDetails.recipient_customer?.id,
            label: `${contractOsDetails.recipient_customer?.id} - ${contractOsDetails.recipient_customer?.alias}`,
          },
          destination_order_id: {
            value: contractOsDetails.destination_order?.id,
            label: contractOsDetails.destination_order?.name || '-',
          },
          address_id: frequentAddress
            ? {
                value: frequentAddress.id,
                label: `${frequentAddress.id} - ${frequentAddress.formatted_address}`,
              }
            : null,
          location_id: contractOsDetails?.items?.[0]?.location
            ? {
                value: contractOsDetails?.items?.[0]?.location?.id,
                label: `${contractOsDetails.items?.[0]?.location?.id} - ${contractOsDetails.items?.[0]?.location?.formatted_location}`,
              }
            : null,
          mode_transport_id: {
            value: contractOsDetails.mode_transport?.id,
            label: contractOsDetails.mode_transport?.name,
          },
          deadline: contractOsDetails.deadline?.split(' ')[0],
          details: contractOsDetails.details,
        },
      }))
    }
  }, [contractOsDetails, order])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Adicionar Item à Requisição`}
        showBackButton={true}
        backUrl={
          contractId
            ? `/aluguel/contratos/${contractId}/requisicoes/`
            : `/aluguel/requisicoes/`
        }
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={AddItemsOsMutation.isPending}>
          {() => (
            <>
              <div key={order.section}>
                <SectionHeader section={order.section} />
                <SimpleBody
                  fields={order.fieldsState}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={order.formData}
                  handleFieldChange={order.handleChange}
                  getOptions={order.getOptions}
                  getSelectedValue={order.getSelectedValue}
                  getSelectMeta={order.getSelectMeta}
                />
              </div>
              <Box
                key={equipments.section}
                sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                {/* EQUIPAMENTOS */}
                <ItemListSection
                  section={equipments.section}
                  fields={equipments.fieldsState}
                  initialData={equipments.data}
                  onItemsChange={equipments.onItemsChange}
                  renderFormBody={equipments.renderFormBody}
                  getItemDisplayValue={equipments.getItemDisplayValue}
                  itemFormData={equipments.formData}
                  onItemChange={equipments.handleChange}
                  itemFormErrors={form.formErrors}
                  onFormSubmit={equipments.onFormSubmit}
                  onDeleteItem={equipments.onDeleteItem}
                  initializeItemForm={equipments.initializeItemForm}
                  captureAction={equipments.captureAction}
                  isDisabledButton={form.disabledButton}
                  getOptions={equipments.getOptions}
                  getSelectedValue={equipments.getSelectedValue}
                  getSelectMeta={equipments.getSelectMeta}
                  notShowHeader={[
                    'equipments.location_id',
                    'equipments.details',
                  ]}
                  headerCustom={() => {
                    if (
                      order.selects.movementType.selectedValue?.value !==
                      MovementEnum.ALUGUEL
                    )
                      return null
                    return (
                      <>
                        <br />
                        {!order.hasBalance && (
                          <Alert severity="warning">
                            <Typography sx={{ fontSize: '1rem' }}>
                              O contrato não possui mais saldo disponível, então
                              ao adicionar um item sem saldo, ele será
                              considerado como excedente de contrato. Será
                              necessário uma autorização da gerência para essa
                              requisição
                            </Typography>
                          </Alert>
                        )}
                        {/* Separator */}
                        <hr />
                      </>
                    )
                  }}
                />
              </Box>

              {/* Somente para aluguel */}
              {[MovementEnum.ALUGUEL, MovementEnum.RESERVA].includes(
                order.selects.movementType.selectedValue?.value
              ) && (
                <>
                  <Box
                    key={extras.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* KIT DE EQUIPAMENTOS */}
                    <ItemListSection
                      section={extras.section}
                      fields={extras.fields}
                      initialData={extras.data}
                      onItemsChange={extras.onItemsChange}
                      renderFormBody={extras.renderFormBody}
                      getItemDisplayValue={extras.getItemDisplayValue}
                      itemFormData={extras.formData}
                      onItemChange={extras.handleChange}
                      itemFormErrors={form.formErrors}
                      onFormSubmit={extras.onFormSubmit}
                      initializeItemForm={extras.initializeItemForm}
                      captureAction={extras.captureAction}
                      isDisabledButton={form.disabledButton}
                      getOptions={extras.getOptions}
                      getSelectedValue={extras.getSelectedValue}
                      getSelectMeta={extras.getSelectMeta}
                    />
                  </Box>
                </>
              )}

              {/* ITEMS */}
              {[
                MovementEnum.ALUGUEL,
                MovementEnum.RESERVA,
                MovementEnum.AVULSO,
              ].includes(order.selects.movementType.selectedValue?.value) && (
                <>
                  <Box
                    key={items.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* ITEMS */}
                    <ItemListSection
                      section={items.section}
                      fields={items.fields}
                      initialData={items.data}
                      onItemsChange={items.onItemsChange}
                      renderFormBody={items.renderFormBody}
                      getItemDisplayValue={items.getItemDisplayValue}
                      itemFormData={items.formData}
                      onItemChange={items.handleChange}
                      itemFormErrors={form.formErrors}
                      onFormSubmit={items.onFormSubmit}
                      initializeItemForm={items.initializeItemForm}
                      captureAction={items.captureAction}
                      isDisabledButton={form.disabledButton}
                      getOptions={items.getOptions}
                      getSelectedValue={items.getSelectedValue}
                      getSelectMeta={items.getSelectMeta}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default AddItemOrderServicePage
